import Configs from "../../settings/Configs";
import IClass from "../../common/IClass";
import Logger from "../../common/utils/Logger";
import { Environment } from "../../common/Environment";
import { ApiPath } from "../../backend/ApiPath";
import { LoginRequest, LoginResponse } from "../../common/Login";
import { AutoLoginRequest, AutoLoginResponse } from "../../common/AutoLogin";
import { SignUpRequest, SignUpResponse } from "../../common/SignUp";
import { TestRequest, TestPrivateResponse, TestPublicResponse } from "../../common/Test";
import MockLocalStorage from "../utils/MockLocalStorage";
import User from "../../common/User";
import { IRequest, IResponse } from "../../common/RequestResponse";
import { ChartData } from "../../common/Chart";
import { Entry, EntryFilter, EntryGetRequest, EntryGetResponse, EntryExportRequest } from "../../common/Entry";
import CommonHelper from "../../common/utils/CommonHelper";
import Report, { ReportStrings, ReportValues } from "../../common/Report";

// only for frontend
import FileSaver from 'file-saver';

class Linker extends IClass {

    private static keyToken: string = `${Configs.STORAGE_PREFIX}_token`;
    private static keyExpiryTimestamp: string = `${Configs.STORAGE_PREFIX}_expiryTimestamp`;
    private static keyUser: string = `${Configs.STORAGE_PREFIX}_user`;

    private env: Environment;

    private MockLocalStorage: MockLocalStorage;

    private get LocalStorage() {
        if (this.MockLocalStorage != undefined) {
            return this.MockLocalStorage;
        }
        return localStorage;
    }

    public get User(): User | undefined {
        let output = this.LocalStorage.getItem(Linker.keyUser);
        if (output) return JSON.parse(output);
        return undefined;
    }

    private set User(input: User | undefined) {
        if (input) {
            this.LocalStorage.setItem(Linker.keyUser, JSON.stringify(input));
        }
        else {
            this.LocalStorage.removeItem(Linker.keyUser);
        }
    }

    private get ExpiryTimestamp(): number | undefined {
        let output = this.LocalStorage.getItem(Linker.keyExpiryTimestamp);
        if (output) return parseInt(output);
        return undefined;
    }

    private set ExpiryTimestamp(input: number | undefined) {
        if (input) {
            this.LocalStorage.setItem(Linker.keyExpiryTimestamp, input.toString());
        }
        else {
            this.LocalStorage.removeItem(Linker.keyExpiryTimestamp);
        }
    }

    private get Token(): string | undefined {
        let output = this.LocalStorage.getItem(Linker.keyToken);
        if (output) return output;
        return undefined;
    }

    private set Token(input: string | undefined) {
        if (input) {
            this.LocalStorage.setItem(Linker.keyToken, input);
        }
        else {
            this.LocalStorage.removeItem(Linker.keyToken);
        }
    }

    constructor() {
        super();
        if (typeof localStorage == 'undefined') {
            this.MockLocalStorage = new MockLocalStorage();
        }
        if (typeof location == 'undefined' || location.hostname == 'localhost') {
            this.env = 'dev';
        } else {
            this.env = 'prod';
        }
    }

    public async Init(): Promise<void> {
        Logger.Log(undefined, `Init`, this);
        if (!this.ExpiryTimestamp || this.ExpiryTimestamp < Math.floor(Date.now() / 1000)) {
            this.User = undefined;
            this.ExpiryTimestamp = undefined;
            this.Token = undefined;
        }
        return Promise.resolve();
    }

    private async CallApi<T, U>(apiPath: ApiPath, params?: T): Promise<U> {
        let url: URL = new URL(`${this.env == 'dev' ? Configs.DEV_URL : Configs.PROD_URL}${apiPath}`);

        let requestInit: RequestInit = {
            method: 'POST',
            body: params && JSON.stringify(params),
            headers: {
                'Content-Type': 'application/json',
                'Token': this.Token || ''
            }
        };
        let response = await fetch(url, requestInit).then(res => res.clone().json());
        return Promise.resolve(response);
    }

    private async CallApiBuffer<T>(apiPath: ApiPath, params?: T): Promise<ArrayBuffer> {

        let url: URL = new URL(`${this.env == 'dev' ? Configs.DEV_URL : Configs.PROD_URL}${apiPath}`);

        let requestInit: RequestInit = {
            method: 'POST',
            body: params && JSON.stringify(params),
            headers: {
                'Content-Type': 'application/json',
                'Token': this.Token || ''
            }
        };
        let response = await fetch(url, requestInit).then(res => {
            return res.clone().arrayBuffer();
        });
        return Promise.resolve(response);
    }

    public async Login(values: LoginRequest): Promise<LoginResponse> {
        let response = await this.CallApi<LoginRequest, LoginResponse>(ApiPath.Login, values);
        if (!response.success) {
            this.Token = undefined;
            this.ExpiryTimestamp = undefined;
            delete this.User;
        } else {
            this.User = {
                email: response.value!.email,
                firstName: response.value!.firstName,
                lastName: response.value!.lastName,
                role: response.value!.role,
            };
            this.Token = response.value!.token;
            this.ExpiryTimestamp = response.value!.expiryTimestamp;
        }
        return response;
    }

    public async SignUp(values: SignUpRequest): Promise<SignUpResponse> {
        return await this.CallApi<SignUpRequest, SignUpResponse>(ApiPath.SignUp, values);
    }

    public async Logout(): Promise<IResponse<{}>> {
        Logger.Log(undefined, `logout`, this);
        this.Token = undefined;
        this.ExpiryTimestamp = undefined;
        this.User = undefined;
        let output: IResponse<{}> = {
            success: true,
            value: {}
        }
        return output;
    }

    public async EntriesGet(input: EntryGetRequest): Promise<EntryGetResponse> {
        let entriesGetResponse = await this.CallApi<EntryGetRequest, EntryGetResponse>(ApiPath.EntriesGet, input);
        return Promise.resolve(entriesGetResponse);
    }

    public async Export(values:EntryExportRequest): Promise<void> {

        let entriesExportResponse = await this.CallApiBuffer<EntryExportRequest>(ApiPath.EntriesExport, values);
        let fileName = `${Date.now()}_feedback.xlsx`;
        FileSaver.saveAs(new Blob([entriesExportResponse]), fileName);

        return Promise.resolve();
    }

    public async TestPrivate(values: TestRequest): Promise<TestPrivateResponse> {
        return await this.CallApi<TestRequest, TestPrivateResponse>(ApiPath.TestPrivate, values);
    }

    public async TestPublic(values: TestRequest): Promise<TestPublicResponse> {
        return await this.CallApi<TestRequest, TestPublicResponse>(ApiPath.TestPublic, values);
    }


}

export default Linker;